// Eager imports — these are tiny shells that always render on the
// desktop or container roots. Dragging them out of the main bundle
// would just add a Suspense flash with no payoff.
import ProgramManager from "../apps/ProgramManager";
import ProgramGroup from "../apps/ProgGroup";
import FunctionsApp from "../apps/FunctionsApp";
import Desk from "../apps/Desk";

// Everything else gets a webpack-named lazy chunk so a fresh page
// load only downloads what the desktop draws first. Each program is
// streamed in when the user actually opens it (or its parent
// container does), which keeps `main.js` from sucking in Radix /
// doritokit / Solana / etc.
import { lazyApp } from "./lazyApp";

const Notepad = lazyApp(
	() => import(/* webpackChunkName: "app-notepad" */ "../apps/Notepad"),
);
const Calculator = lazyApp(
	() => import(/* webpackChunkName: "app-calculator" */ "../apps/Calculator"),
);
const Clock = lazyApp(
	() => import(/* webpackChunkName: "app-clock" */ "../apps/Clock"),
);
const Paintbrush = lazyApp(
	() => import(/* webpackChunkName: "app-paintbrush" */ "../apps/Paintbrush"),
);
const Winbit32 = lazyApp(
	() => import(/* webpackChunkName: "app-winbit32" */ "../apps/winbit32/Winbit32"),
);
const Portfolio = lazyApp(
	() => import(/* webpackChunkName: "app-portfolio" */ "../apps/winbit32/Portfolio"),
);
const NFPTViewer = lazyApp(
	() => import(/* webpackChunkName: "app-nfpt" */ "../apps/winbit32/NFPTViewer"),
);
// NFPT Market (nfptmarket.exe) is DISABLED for release — the UI isn't up to
// scratch. The kit-level buy/sell/mint flows (@winbit32/wallet-kit src/nfpt/)
// stay live via the Gopher buy panel; the desktop marketplace returns as a
// total rewrite in Winbit32NT (see ROADMAP.md "NT port notes").
const MessengerApp = lazyApp(
	() => import(/* webpackChunkName: "app-messenger" */ "../apps/winbit32/messenger/MessengerApp"),
);
const X402PayComponent = lazyApp(
	() => import(/* webpackChunkName: "app-x402pay" */ "../apps/winbit32/x402/X402PayComponent"),
);
const IRCWindow = lazyApp(
	() => import(/* webpackChunkName: "app-irc" */ "../apps/winbit32/mirc/IRCWindow"),
);
const ChannelList = lazyApp(
	() => import(/* webpackChunkName: "app-irc" */ "../apps/winbit32/mirc/ChannelList"),
);
const MessagePanel = lazyApp(
	() => import(/* webpackChunkName: "app-irc" */ "../apps/winbit32/mirc/MessagePanel"),
);
const SwapComponent = lazyApp(
	() => import(/* webpackChunkName: "app-swap" */ "../apps/winbit32/SwapComponent"),
);
const SendFundsComponent = lazyApp(
	() => import(/* webpackChunkName: "app-send-funds" */ "../apps/winbit32/SendFundsComponent"),
);
const SecTools = lazyApp(
	() => import(/* webpackChunkName: "app-sectools" */ "../apps/sectools/SecTools"),
);
const Split = lazyApp(
	() => import(/* webpackChunkName: "app-sectools-split" */ "../apps/sectools/Split"),
);
const Unsplit = lazyApp(
	() => import(/* webpackChunkName: "app-sectools-unsplit" */ "../apps/sectools/Unsplit"),
);
const RngTestApp = lazyApp(
	() => import(/* webpackChunkName: "app-sectools-rngtest" */ "../apps/sectools/RngTestApp"),
);
const EntropyApp = lazyApp(
	() => import(/* webpackChunkName: "app-sectools-entropy" */ "../apps/sectools/EntropyApp"),
);
const PhraseApp = lazyApp(
	() => import(/* webpackChunkName: "app-sectools-phrase" */ "../apps/sectools/PhraseApp"),
);
const ViewQR = lazyApp(
	() => import(/* webpackChunkName: "app-viewqr" */ "../apps/winbit32/ViewQR"),
);
const ReadQR = lazyApp(
	() => import(/* webpackChunkName: "app-readqr" */ "../apps/winbit32/ReadQR"),
);
const PhraseHunter = lazyApp(
	() => import(/* webpackChunkName: "app-phrasehunter" */ "../apps/sectools/PhraseHunter"),
);
const AddressGenerator = lazyApp(
	() => import(/* webpackChunkName: "app-addrgen" */ "../apps/sectools/AddressGenerator"),
);
const AddressFinder = lazyApp(
	() => import(/* webpackChunkName: "app-addrfinder" */ "../apps/sectools/AddressFinder"),
);
const NFTPurchasingComponent = lazyApp(
	() => import(/* webpackChunkName: "app-nft" */ "../apps/winbit32/mnft/NFTPurchasingComponent"),
);
const SignTransactionComponent = lazyApp(
	() => import(/* webpackChunkName: "app-signtx" */ "../apps/winbit32/SignTransactionComponent"),
);
const ShamirConnect = lazyApp(
	() => import(/* webpackChunkName: "app-shamir-connect" */ "../apps/winbit32/ShamirConnect"),
);
const License = lazyApp(
	() => import(/* webpackChunkName: "app-license" */ "../apps/winbit32/License"),
);
const PoolComponent = lazyApp(
	() => import(/* webpackChunkName: "app-pool" */ "../apps/winbit32/PoolComponent"),
);
const BankComponent = lazyApp(
	() => import(/* webpackChunkName: "app-bank" */ "../apps/winbit32/BankComponent/BankComponent"),
);
const JupiterSwapComponent = lazyApp(
	() => import(/* webpackChunkName: "app-jupiter" */ "../apps/winbit32/solswap/JupiterSwapComponent"),
);
const VTools = lazyApp(
	() => import(/* webpackChunkName: "app-vtools" */ "../apps/sectools/vtools/VTools"),
);
const DisplayProperties = lazyApp(
	() => import(/* webpackChunkName: "app-display" */ "../apps/controlpanel/DisplayProperties"),
);
const RareSats = lazyApp(
	() => import(/* webpackChunkName: "app-raresats" */ "../apps/RareSats"),
);
const ExoraSwapComponent = lazyApp(
	() => import(/* webpackChunkName: "app-exora" */ "../apps/winbit32/Exora"),
);
const ZcashTestHarness = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-test" */ "../apps/winbit32/ZcashTestHarness"),
);
const ZcashWizardGUI = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-send" */ "../apps/winbit32/ZcashWizardGUI"),
);
const ZcashPrompt = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-cli" */ "../apps/winbit32/ZcashPrompt"),
);
const ZcashTxInspector = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-tx" */ "../apps/winbit32/ZcashTxInspector"),
);
const ZcashApp = lazyApp(
	() => import(/* webpackChunkName: "app-zcash" */ "../apps/winbit32/ZcashApp"),
);
const ZcashReceiveWizard = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-receive" */ "../apps/winbit32/ZcashReceiveWizard"),
);
const MoneroReceiveWizard = lazyApp(
	() => import(/* webpackChunkName: "app-monero-receive" */ "../apps/winbit32/MoneroReceiveWizard"),
);
const MoneroSendWizard = lazyApp(
	() => import(/* webpackChunkName: "app-monero-send" */ "../apps/winbit32/MoneroSendWizard"),
);
const MoneroTxInspector = lazyApp(
	() => import(/* webpackChunkName: "app-monero-tx" */ "../apps/winbit32/MoneroTxInspector"),
);
// DashPrivacyWizard was unregistered while it could only VIEW CoinJoin
// denominations; registered since Dash activated Orchard shielded pools on
// Evolution (17 Jul 2026) — it now also reads the live shielded pool and
// understands Platform dash1… addresses.
const DashPrivacyWizard = lazyApp(
	() => import(/* webpackChunkName: "app-dash-privacy" */ "../apps/winbit32/DashPrivacyWizard"),
);
// The old standalone "Zcash Amounts" advisor now lives inside the Bus
// Station wizard as step 1 (zcashbus.exe) — one app, less box soup.
const ZcashBusWizard = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-bus" */ "../apps/winbit32/ZcashBusWizard"),
);
const ZcashPurse = lazyApp(
	() => import(/* webpackChunkName: "app-zcash-purse" */ "../apps/winbit32/purse/ZcashPurse"),
);
const IronwoodWizard = lazyApp(
	() => import(/* webpackChunkName: "app-ironwood" */ "../apps/winbit32/IronwoodWizard"),
);
const ZecStatsApp = lazyApp(
	() => import(/* webpackChunkName: "app-zecstats" */ "../apps/winbit32/ZecStatsApp"),
);
const HelpApp = lazyApp(
	() => import(/* webpackChunkName: "app-help" */ "../apps/winbit32/HelpApp"),
);
const CosignerApp = lazyApp(
	() => import(/* webpackChunkName: "app-cosigner" */ "../apps/winbit32/CosignerApp"),
);
const CreateVaultWizard = lazyApp(
	() => import(/* webpackChunkName: "app-create-vault" */ "../apps/winbit32/CreateVaultWizard"),
);
const GopherClient = lazyApp(
	() => import(/* webpackChunkName: "app-gopher" */ "../apps/gopher/GopherClient"),
);

const showZcashTestHarness =
	process.env.REACT_APP_ENABLE_ZCASH_TEST_HARNESS === "true" ||
	process.env.NODE_ENV === "development";

const shouldAutoOpenZcashTests = (() => {
	if (typeof window === "undefined") return false;
	try {
		const params = new URLSearchParams(window.location.search);
		const mode = (params.get("zcashTestAutoRun") || "").toLowerCase();
		return mode === "1" || mode === "true" || mode === "all" || mode === "transparent" || mode === "shielded";
	} catch (_error) {
		return false;
	}
})();

const autoOpenZcashTestHarness =
	showZcashTestHarness &&
	(shouldAutoOpenZcashTests || process.env.REACT_APP_ZCASH_TEST_AUTORUN === "true");

export const getPrograms = () => {
	return [
		{
			progID: 0,
			title: "Program Manager",
			progName: "progman.exe", // Added name for "Notepad
			minimized: false,
			maximized: false,
			component: ProgramManager,
			icon: "💾", // You can use emojis or custom icons
			invisibleToHash: true,
			params: { _onOpenWindow: "!!handleOpenWindow" },
			// Room for the open Crypto group plus the row of minimised group
			// icons — Accessories, Tools and AI — beneath it.
			initialPosition: { x: 5, y: 5, width: 395, height: 320, smHeight: 305 },
			defaultOpen: true,
			isContainer: true,
			unCloseable: true,
			programs: [
				{
					progID: 1,
					title: "Accessories",
					progName: "acc.exe", // Added name for "Notepad
					minimized: true,
					maximized: false,
					invisibleToHash: true,
					component: ProgramGroup,
					icon: "▦", // You can use emojis or custom icons
					params: { _onOpenWindow: "!!handleOpenWindow" },
					initialPosition: {
						x: 5,
						y: 5,
						width: 355,
						height: 250,
					},

					defaultOpen: true,
					unCloseable: true,
					programs: [
						{
							progID: 1,
							title: "Notepad",
							icon: "📝", // You can use emojis or custom icons
							progName: "notepad.exe", // Added name for "Notepad
							minimized: false,
							maximized: false,
							component: Notepad,
							initialPosition: {
								x: 10,
								y: 10,
								width: 720,
								height: 480,
								smWidth: 380,
								smHeight: 300,
							},
						},
						{
							progID: 2,
							title: "Calculator",
							icon: "🧮", // Example icon
							progName: "calc.exe", // Added name for "Calculator"
							component: Calculator,
						},
						{
							progID: 3,
							title: "Clock",
							icon: "⏰", // Example icon
							progName: "clock.exe", // Added name for "File Manager"
							component: Clock,
							defaultOpen: false,
							initialPosition: {
								x: 1,
								y: 1,
								width: 250,
								height: 250,
								smHeight: 250,
							},
						},
						{
							progID: 4,
							title: "Paintbrush",
							icon: "🎨", // Example icon
							progName: "paint.exe", // Added name for "Paintbrush"
							component: Paintbrush,
							initialPosition: { x: 1, y: 1, width: 425, height: 485 },
						},
						{
							progID: 5,
							title: "Help",
							icon: "❓",
							progName: "winhelp.exe",
							component: HelpApp,
							initialPosition: {
								x: "auto",
								y: 0,
								width: 560,
								height: 480,
								smHeight: 380,
								smWidth: 340,
							},
						},
					],
				},
				{
					progID: 2,
					title: "Crypto",
					progName: "crypto.exe", // Added name for "Notepad
					minimized: false,
					maximized: false,
					component: ProgramGroup,
					icon: "▦", // You can use emojis or custom icons
					params: { _onOpenWindow: "!!handleOpenWindow" },
					initialPosition: {
						x: 5,
						y: 5,
						width: 355,
						height: 150,
						smHeight: 140,
					},
					defaultOpen: true,
					invisibleToHash: true,
					unCloseable: true,
					programs: [
						// {
						// 	progID: 8,
						// 	title: "Rare Sats",
						// 	icon: "💎", // Example ico
						// 	progName: "raresats.exe",
						// 	component: RareSats,
						// 	initialPosition: { x: 1, y: 1, width: 425, height: 485 },
						// 	maximised: true,
						// },
					{
						progID: 22,
						title: "Privacy Suite",
						icon: "🛡️",
						progName: "zcashprog.exe",
							hashName: "Privacy",
							component: ZcashApp,
							maximized: true,
							addProgramData: ["phrase"],
							/* Sized so each group's 3 tiles fill the row at
							   their comfortable max width with no empty void
							   to the right, and the last row isn't clipped. */
							initialPosition: {
								x: "auto",
								y: 0,
								width: 580,
								/* +70px over the pre-Ironwood 510 so the new
								   NU7 shelf doesn't clip the last row. */
								height: 580,
								smHeight: 510,
								smWidth: 350,
							},
							programs: [
								{
									progID: 0,
									title: "Zcash Wizard",
									titleNote: "Guided",
									icon: "📤",
									progName: "zcashwzrd.exe",
									component: ZcashWizardGUI,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 640,
										smHeight: 520,
										smWidth: 360,
									},
								},
								{
									progID: 1,
									title: "Privacy Prompt",
									titleNote: "CLI",
									icon: "⌨",
									progName: "zcashcli.exe",
									component: ZcashPrompt,
									maximized: false,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 700,
										height: 500,
										smHeight: 400,
										smWidth: 360,
									},
								},
							{
								progID: 2,
								title: "TX Inspector",
								titleNote: "Info",
								icon: "🔍",
								progName: "zcashtxi.exe",
							component: ZcashTxInspector,
							maximized: false,
							hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 600,
									height: 550,
									smHeight: 450,
									smWidth: 350,
								},
							},
							{
								progID: 3,
								title: "Zcash View & Receive",
								titleNote: "ZEC",
								icon: "📥",
								progName: "zcashrecv.exe",
								component: ZcashReceiveWizard,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 640,
									height: 640,
									smHeight: 520,
									smWidth: 360,
								},
							},
							{
								progID: 4,
								title: "Monero View & Receive",
								titleNote: "XMR",
								icon: "📥",
								progName: "monerorecv.exe",
								component: MoneroReceiveWizard,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 680,
									height: 720,
									smHeight: 560,
									smWidth: 360,
								},
							},
							{
								progID: 5,
								title: "Monero Send",
								titleNote: "XMR",
								icon: "📤",
								progName: "monerosend.exe",
								component: MoneroSendWizard,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 640,
									height: 640,
									smHeight: 520,
									smWidth: 360,
								},
							},
							{
								progID: 6,
								title: "Monero TX Inspector",
								titleNote: "Info",
								icon: "🔍",
								progName: "monerotxi.exe",
								component: MoneroTxInspector,
								maximized: false,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 600,
									height: 560,
									smHeight: 460,
									smWidth: 350,
								},
							},
							{
								// Zcash "Bus Station" — the blend-in amount advisor and the
								// non-custodial mixing board merged into one three-step wizard:
								// pick an amount that blends in (live popularity + round-trip
								// self-dox check), choose/start a bus (rendezvous to cross the
								// pool boundary together), board & depart. Standalone the
								// advisor works with no wallet; under Money Manager
								// "Broadcast your swap" prefills the private-exit Exchange
								// swap so you ride out with the cohort.
								progID: 11,
								title: "Zcash Bus Station",
								titleNote: "Blend & Mix",
								icon: "🚌",
								progName: "zcashbus.exe",
								component: ZcashBusWizard,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 640,
									height: 700,
									smHeight: 560,
									smWidth: 360,
								},
							},
							{
								// "My Zcash Purse (for girls!!)" — a fully working
								// shielded wallet in deliberate 1996 pink-it-and-shrink-it
								// parody. Every purse-speak term shows the real jargon in
								// small print, so the OTT copy doubles as the clearest
								// onboarding we ship. Phrase wallets only (vaults → Wizard).
								progID: 12,
								title: "Zcash Purse",
								titleNote: "for girls!!",
								icon: "👛",
								progName: "purse.exe",
								component: ZcashPurse,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 640,
									height: 700,
									smHeight: 560,
									smWidth: 360,
								},
							},
							{
								// Chain-derived NFPT browser + transfers (rows from
								// the NFPT wallet-scanner host). Standalone the wallet
								// step offers a phrase, .vult/.wult shares or a UFVK;
								// when the suite is opened under Money Manager it
								// derives the shielded owner address automatically
								// (and the wallet step only offers the UFVK option —
								// Money Manager owns the vault/phrase connection).
								// hideInToolbar like every other suite tool — the
								// Control Panel grid is the launcher; without this the
								// container toolbar renders a lone "NFPTs" button.
								progID: 8,
								title: "NFPTs",
								titleNote: "Zcash",
								icon: "🖼️",
								progName: "nfpt.exe",
								component: NFPTViewer,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 760,
									height: 620,
									smHeight: 520,
									smWidth: 350,
								},
							},
							{
								progID: 7,
								title: "Vault Wizard",
								titleNote: "Create · Convert · Upgrade",
								icon: "🛠️",
								progName: "createvault.exe",
								component: CreateVaultWizard,
								maximized: false,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 700,
									height: 620,
									smHeight: 540,
									smWidth: 360,
								},
							},
							{
								// Ironwood (NU7) readiness wizard — the send wizard
								// with recipient/amount cut out and a per-pool
								// "privacy checkup" put in. Every action is a
								// self-send (shield transparent → Orchard, Sapling
								// → Orchard); the Orchard → Ironwood checkpoint
								// crossing unlocks when WebZjs supports it.
								progID: 13,
								title: "Check & Upgrade",
								titleNote: "Ironwood",
								icon: "🪵",
								progName: "ironwood.exe",
								component: IronwoodWizard,
								maximized: true,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 640,
									height: 660,
									smHeight: 540,
									smWidth: 360,
								},
							},
							{
								// Dash privacy: CoinJoin denomination view (Core
								// chain, watch-only) + the live Orchard shielded
								// pool on Evolution (evo-sdk, loaded on demand).
								progID: 15,
								title: "Dash Privacy",
								titleNote: "CoinJoin · Orchard",
								icon: "💠",
								progName: "dashprivacy.exe",
								component: DashPrivacyWizard,
								maximized: false,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 640,
									height: 660,
									smHeight: 540,
									smWidth: 360,
								},
							},
							{
								// Ironwood Observatory — NU7 countdown + headline
								// chain stats (Blockchair, CORS-open) above an
								// embedded zecstats.com panel, Gopher-preview style.
								progID: 14,
								title: "ZEC Stats",
								titleNote: "Observatory",
								icon: "🔭",
								progName: "zecstats.exe",
								component: ZecStatsApp,
								maximized: false,
								hideInToolbar: true,
								initialPosition: {
									x: "auto",
									y: 0,
									width: 720,
									height: 640,
									smHeight: 540,
									smWidth: 350,
								},
							},
						],
					},

				{
					progID: 23,
					title: "Cosigner",
					titleNote: "MPC",
					icon: "🤝",
					progName: "cosign.exe",
					component: CosignerApp,
					maximized: false,
					initialPosition: {
						x: "auto",
						y: 0,
						width: 400,
						height: 520,
						smHeight: 780,
						smWidth: 420,
					},
				},

					{
						progID: 7,
						title: "Money Manager",
							embededTitle: "WINBIT32.COM",
							embedable: true,
							icon: "💰",
							progName: "winbit32.exe",
							component: Winbit32,
							maximized: true,
									defaultOpen: autoOpenZcashTestHarness,
							//isContainer: true,
							programs: [
								{
									// All-apps launcher: lists every Money Manager tool
									// in a scrollable grid so they stay reachable when the
									// mobile toolbar collapses to icons. Container-agnostic
									// (renders whatever `programs` its WindowManager passes).
									// No toolbar button of its own — it IS the launcher:
									// it opens by itself when Money Manager starts empty
									// (no #hash route, nothing restored), and stays
									// reachable from the Program menu after that.
									progID: 33,
									title: "Functions",
									titleNote: "All apps",
									icon: "🧰",
									progName: "functions.exe",
									component: FunctionsApp,
									hideInToolbar: true,
									defaultOpenWhenEmpty: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 480,
										height: 520,
										smWidth: 340,
										smHeight: 460,
									},
								},
								{
									progID: 0,
									title: "Portfolio",
									icon: "💼", // Example icon
									progName: "portfolio.exe", // Added name for "Calculator"
									component: Portfolio,
									maximized: true,
								},
								// NFPT Market (progID 34, nfptmarket.exe) is disabled for
								// this release — see the note beside the removed lazy
								// import at the top of this file. Re-landing it is an NT
								// job (total rewrite), not a re-enable here.
								// NFPTs live in the Privacy Suite only (nfpt.exe under
								// zcashprog.exe). Under Money Manager the suite is opened
								// via Portfolio and inherits Money Manager's programData,
								// so the viewer still derives the owner address from the
								// connected phrase/vault automatically — without a second
								// toolbar tile here.
								...(showZcashTestHarness ? [{
									progID: 21,
									title: "Zcash Tests",
									titleNote: "Dev",
									icon: "🧪",
									progName: "zcashtest.exe",
									component: ZcashTestHarness,
									defaultOpen: autoOpenZcashTestHarness,
									hideInToolbar: true,
									// Dev harness: reachable by hash/autorun only, never
									// listed in the user-facing Functions launcher.
									hideInFunctions: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 520,
										height: 560,
										smHeight: 420,
									},
								}] : []),
								{
									progID: 1,
									title: "Exchange",
									icon: "🔀",
									progName: "exchange.exe", // Added name for "File Manager"
									component: SwapComponent,
									defaultOpen: false,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 400,
										height: 675,
										smHeight: 350,
									},
								},
								{
									progID: 1,
									title: "Exora",
									titleNote: "Beta",
									icon: "🔢",
									progName: "exora.exe", // Added name for "File Manager"
									component: ExoraSwapComponent,
									defaultOpen: false,
									maximized: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 850,
										height: 675,
										smHeight: 500,
										smWidth: 350,
									},
								},
								{
									progID: 2,
									title: "Send",
									icon: "✉️",
									progName: "send.exe", // Added name for "Paintbrush"
									component: SendFundsComponent,
									initialPosition: {
										x: 1,
										y: 1,
										width: 425,
										height: 550,
										smHeight: 350,
									},
								},
								{
									progID: 3,
									title: "Sign",
									icon: "📝",
									progName: "sign.exe", // Added name for "Paintbrush"
									component: SignTransactionComponent,
									initialPosition: {
										x: 1,
										y: 1,
										width: 425,
										height: 550,
										smHeight: 350,
									},
								},

								// {
								// 	progID: 24,
								// 	title: "Bank",
								// 	icon: "🏦",
								// 	progName: "bank.exe", // Added name for "Paintbrush"
								// 	component: BankComponent,
								// 	initialPosition: {
								// 		x: 1,
								// 		y: 1,
								// 		width: 425,
								// 		height: 550,
								// 		smHeight: 350,
								// 	},
								// },

								{
									progID: 9,
									title: "Pots",
									icon: "🍯",
									progName: "swappots.exe", // Added name for "Paintbrush"
									component: PoolComponent,
									initialPosition: {
										x: 1,
										y: 1,
										width: 425,
										height: 550,
										smHeight: 350,
									},
								},
								{
									progID: 30,
									title: "Vault Wizard",
									titleNote: "Create · Convert · Upgrade",
									icon: "🛠️",
									progName: "createvault.exe",
									component: CreateVaultWizard,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 600,
										smHeight: 540,
										smWidth: 360,
									},
								},
								{
									// Same component as the standalone Accessories
									// entry; opened here it inherits Money Manager's
									// providerKey (nested WindowManagers share the
									// SAME SK wallet bucket), so the chat AND its
									// desktop tools (app_wallet_balance, prepare
									// swap/send) see and act on the loaded vault
									// without the user re-connecting anything.
									progID: 31,
									title: "Messenger",
									titleNote: "AI",
									icon: "💬",
									progName: "messenger.exe",
									component: MessengerApp,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 480,
										smWidth: 360,
										smHeight: 460,
									},
								},
								{
									// Pay any x402 endpoint from the loaded vault
									// (non-custodial: the vault signs the EIP-3009
									// authorisation). Inherits Money Manager's
									// programData + SK wallets via providerKey.
									progID: 32,
									title: "Pay (x402)",
									titleNote: "USDC",
									icon: "⚡",
									progName: "x402pay.exe",
									component: X402PayComponent,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 480,
										height: 520,
										smWidth: 340,
										smHeight: 480,
									},
								},
								// {
								// 	progID: 3,
								// 	title: "mayaIRC",
								// 	icon: "📡",
								// 	progName: "mirc.exe", // Added name for "Paintbrush"
								// 	component: IRCWindow,
								// 	initialPosition: { x: 1, y: 1, width: 425, height: 485 },
								// 	programs: [
								// 		{
								// 			progID: 0,
								// 			title: "Channel List",
								// 			//unicode icon:
								// 			icon: "📃",
								// 			component: ChannelList,
								// 			progName: "clist.exe", // Added name for "Channel List
								// 			defaultOpen: true,
								// 			maximized: true,
								// 			params: { _onOpenWindow: "!!handleOpenWindow" },
								// 		},
								// 		{
								// 			progID: 1,
								// 			title: "Message Panel",
								// 			icon: "💬",
								// 			progName: "msgpanel.exe", // Added name for "Message Panel"
								// 			component: MessagePanel,
								// 			maximized: true,
								// 			defaultOpen: false,
								// 		},
								// 	],
								// },
								// {
								// 	progID: 40,
								// 	title: "SolSwap",
								// 	icon: "📇",
								// 	progName: "solswap.exe", // Added name for "Paintbrush"
								// 	component: JupiterSwapComponent,
								// 	maximized: true,
								// },
								// {
								// 	progID: 4,
								// 	title: "mayaNFT",
								// 	icon: "📇",
								// 	progName: "mnft.exe", // Added name for "Paintbrush"
								// 	component: NFTPurchasingComponent,
								// 	maximized: true,
								// },
								// {
								// 	progID: 5,
								// 	title: "License",
								// 	icon: "📟",
								// 	progName: "license.exe", // Added name for "Paintbrush"
								// 	component: License,
								// 	maximized: true,
								// },
						{
							progID: 10,
							title: "Help",
							icon: "❓",
							progName: "winhelp.exe",
							component: HelpApp,
							hideInToolbar: true,
							initialPosition: {
								x: "auto",
								y: 0,
								width: 560,
								height: 480,
								smHeight: 380,
								smWidth: 340,
							},
							},
							{
								progID: 8,
								title: "Shamir Connect",
								icon: "🔐",
								hideInToolbar: true,
								progName: "sss.exe",
								component: ShamirConnect,
									addProgramData: ["phrase"],
									initialPosition: {
										x: 1,
										y: 1,
										width: 355,
										height: 555,
									},
								},
								{
									progID: 4,
									title: "View Master Seed",
									icon: "💰",
									hideInToolbar: true,
									progName: "viewqr.exe", // Added name for "Paintbrush"
									component: ViewQR,
									addProgramData: ["phrase"],
									initialPosition: {
										x: 1,
										y: 1,
										width: 355,
										height: 285,
									},
								},
								{
									progID: 4,
									title: "Read Master Seed",
									icon: "💰",
									hideInToolbar: true,
									progName: "readqr.exe", // Added name for "Paintbrush"
									component: ReadQR,
									addProgramData: ["setPhrase", "setStatusMessage"],
									initialPosition: {
										x: 1,
										y: 1,
										width: 325,
										height: 325,
									},
								},
							{
								progID: 20,
								title: "Privacy",
								titleNote: "Suite",
								icon: "🛡️",
								progName: "zcashprog.exe",
								hashName: "Privacy",
								component: ZcashApp,
								addProgramData: ["phrase"],
								maximized: true,
								/* Matches the top-level Privacy Suite launcher
								   — sized so 3 tiles fill each group's row
								   without an empty grey strip to the right or
								   under the last row. */
								initialPosition: {
									x: "auto",
									y: 0,
									width: 580,
									/* +70px for the Ironwood NU7 shelf. */
									height: 580,
									smHeight: 510,
									smWidth: 350,
								},
								programs: [
									{
										progID: 0,
										title: "Zcash Wizard",
										titleNote: "Guided",
										icon: "📤",
										progName: "zcashwzrd.exe",
										component: ZcashWizardGUI,
										maximized: true,
										hideInToolbar: true,
										initialPosition: {
											x: "auto",
											y: 0,
											width: 640,
											height: 640,
											smHeight: 520,
											smWidth: 360,
										},
									},
									{
										progID: 1,
										title: "Privacy Prompt",
										titleNote: "CLI",
										icon: "⌨",
										progName: "zcashcli.exe",
										component: ZcashPrompt,
										maximized: false,
										hideInToolbar: true,
										initialPosition: {
											x: "auto",
											y: 0,
											width: 700,
											height: 500,
											smHeight: 400,
											smWidth: 360,
										},
									},
								{
									progID: 2,
									title: "TX Inspector",
									titleNote: "Info",
									icon: "🔍",
									progName: "zcashtxi.exe",
									component: ZcashTxInspector,
									maximized: false,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 600,
										height: 550,
										smHeight: 450,
										smWidth: 350,
									},
								},
								{
									progID: 3,
									title: "Zcash View & Receive",
									titleNote: "ZEC",
									icon: "📥",
									progName: "zcashrecv.exe",
									component: ZcashReceiveWizard,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 640,
										smHeight: 520,
										smWidth: 360,
									},
								},
								{
									progID: 4,
									title: "Monero View & Receive",
									titleNote: "XMR",
									icon: "📥",
									progName: "monerorecv.exe",
									component: MoneroReceiveWizard,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 680,
										height: 720,
										smHeight: 560,
										smWidth: 360,
									},
								},
								{
									progID: 5,
									title: "Monero Send",
									titleNote: "XMR",
									icon: "📤",
									progName: "monerosend.exe",
									component: MoneroSendWizard,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 640,
										smHeight: 520,
										smWidth: 360,
									},
								},
								{
									progID: 6,
									title: "Monero TX Inspector",
									titleNote: "Info",
									icon: "🔍",
									progName: "monerotxi.exe",
									component: MoneroTxInspector,
									maximized: false,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 600,
										height: 560,
										smHeight: 460,
										smWidth: 350,
									},
								},
								{
									// Zcash "Bus Station" — blend-in advisor + mixing board
									// in one stepped wizard (see the standalone suite entry).
									// Under Money Manager "Broadcast your swap" prefills the
									// private-exit Exchange swap so you leave with the cohort.
									progID: 11,
									title: "Zcash Bus Station",
									titleNote: "Blend & Mix",
									icon: "🚌",
									progName: "zcashbus.exe",
									component: ZcashBusWizard,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 700,
										smHeight: 560,
										smWidth: 360,
									},
								},
								{
									// "My Zcash Purse (for girls!!)" — see the
									// top-level Privacy Suite entry. Under Money Manager
									// it inherits the connected phrase via programData.
									progID: 12,
									title: "Zcash Purse",
									titleNote: "for girls!!",
									icon: "👛",
									progName: "purse.exe",
									component: ZcashPurse,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 700,
										smHeight: 560,
										smWidth: 360,
									},
								},
								{
									// NFPT browser — same entry as the standalone suite so
									// the Control Panel shows the tile here too. Under Money
									// Manager the viewer derives the shielded owner address
									// from the connected phrase/vault automatically.
									progID: 8,
									title: "NFPTs",
									titleNote: "Zcash",
									icon: "🖼️",
									progName: "nfpt.exe",
									component: NFPTViewer,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 760,
										height: 620,
										smHeight: 520,
										smWidth: 350,
									},
								},
								{
									progID: 7,
									title: "Vault Wizard",
									titleNote: "Create · Convert · Upgrade",
									icon: "🛠️",
									progName: "createvault.exe",
									component: CreateVaultWizard,
									maximized: false,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 700,
										height: 620,
										smHeight: 540,
										smWidth: 360,
									},
								},
								{
									// Ironwood Check & Upgrade — same entry as the
									// standalone suite; under Money Manager it inherits
									// the connected phrase/vault via programData.
									progID: 13,
									title: "Check & Upgrade",
									titleNote: "Ironwood",
									icon: "🪵",
									progName: "ironwood.exe",
									component: IronwoodWizard,
									maximized: true,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 660,
										smHeight: 540,
										smWidth: 360,
									},
								},
								{
									// Dash Privacy — same entry as the standalone
									// suite; under Money Manager it auto-uses the
									// connected Dash wallet's address.
									progID: 15,
									title: "Dash Privacy",
									titleNote: "CoinJoin · Orchard",
									icon: "💠",
									progName: "dashprivacy.exe",
									component: DashPrivacyWizard,
									maximized: false,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 640,
										height: 660,
										smHeight: 540,
										smWidth: 360,
									},
								},
								{
									// Ironwood Observatory — see the standalone suite entry.
									progID: 14,
									title: "ZEC Stats",
									titleNote: "Observatory",
									icon: "🔭",
									progName: "zecstats.exe",
									component: ZecStatsApp,
									maximized: false,
									hideInToolbar: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 720,
										height: 640,
										smHeight: 540,
										smWidth: 350,
									},
								},
							],
						},
						],
					},
				],
			},
				{
					progID: 4,
					title: "Tools",
					progName: "tools.exe",
					minimized: true,
					maximized: false,
					invisibleToHash: true,
					component: ProgramGroup,
					icon: "▦",
					params: { _onOpenWindow: "!!handleOpenWindow" },
					initialPosition: {
						x: 5,
						y: 5,
						width: 355,
						height: 150,
						smHeight: 140,
					},
					defaultOpen: true,
					unCloseable: true,
					programs: [
						{
							progID: 10,
							title: "Control Panel",
							icon: "⚙️",
							progName: "control.exe",
							component: ProgramGroup,
							invisibleToHash: true,
							params: { _onOpenWindow: "!!handleOpenWindow" },
							initialPosition: {
								x: 20,
								y: 20,
								width: 280,
								height: 150,
							},
							programs: [
								{
									progID: 1,
									title: "Display",
									icon: "🖥️",
									progName: "display.exe",
									component: DisplayProperties,
									// Fits all three group boxes at once, with room for
									// the saver carrying the most Setup options
									// (Toxic Swamp's four); below this it scrolls.
									initialPosition: {
										x: "auto",
										y: 0,
										width: 560,
										height: 500,
										smHeight: 440,
										smWidth: 340,
									},
								},
							],
						},
						{
							progID: 6,
							title: "Security Tools",
							icon: "🔒",
							progName: "sectools.exe",
							component: SecTools,
							maximized: true,
							programs: [
								{
									// Same empty-state launcher Money Manager uses:
									// a grid of every Security Tools program so the
									// suite does not dump Seed Phrase + Split on the
									// desk the moment it opens.
									progID: 10,
									title: "Functions",
									titleNote: "All tools",
									icon: "🧰",
									progName: "functions.exe",
									component: FunctionsApp,
									hideInToolbar: true,
									defaultOpenWhenEmpty: true,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 480,
										height: 520,
										smWidth: 340,
										smHeight: 460,
									},
								},
								{
									progID: 7,
									title: "Seed Phrase",
									icon: "🌱",
									progName: "phrase.exe",
									component: PhraseApp,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: 0,
										y: 0,
										width: 480,
										height: 260,
										smHeight: 240,
									},
								},
								{
									progID: 8,
									title: "Entropy",
									icon: "🎲",
									progName: "entropy.exe",
									component: EntropyApp,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 480,
										height: 640,
										smHeight: 420,
									},
								},
								{
									progID: 9,
									title: "RNG Test Lab",
									icon: "📊",
									progName: "rngtest.exe",
									component: RngTestApp,
									defaultOpen: false,
									initialPosition: {
										x: "auto",
										y: 0,
										width: 560,
										height: 660,
										smHeight: 440,
									},
								},
								{
									progID: 0,
									title: "Split",
									icon: "✂️",
									progName: "split.exe",
									component: Split,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 500,
										height: 560,
										smHeight: 380,
									},
								},
								{
									progID: 1,
									title: "Unsplit",
									icon: "🔗",
									progName: "unsplit.exe",
									component: Unsplit,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 375,
										height: 650,
										smHeight: 350,
									},
								},
								{
									progID: 5,
									title: "Hunter",
									icon: "🕵️",
									progName: "phunter.exe",
									component: PhraseHunter,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 520,
										height: 640,
										smHeight: 480,
										smWidth: 360,
									},
								},
								{
									progID: 4,
									title: "Vult Tools",
									icon: "🔐",
									progName: "vtools.exe",
									component: VTools,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 520,
										height: 700,
										smHeight: 400,
									},
								},
								{
									progID: 3,
									title: "Multi Gen",
									icon: "🔑",
									progName: "addrgen.exe",
									component: AddressGenerator,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 800,
										height: 700,
										smHeight: 500,
										smWidth: 600,
									},
								},
								{
									progID: 6,
									title: "Address Finder",
									icon: "🔎",
									progName: "addrfind.exe",
									component: AddressFinder,
									defaultOpen: false,
									addProgramData: ["phrase"],
									initialPosition: {
										x: "auto",
										y: 0,
										width: 900,
										height: 700,
										smHeight: 500,
										smWidth: 600,
									},
								},
								{
									progID: 2,
									title: "Open...",
									icon: "💼",
									progName: "winbit32.exe",
									openLevel: -1,
									addProgramData: ["phrase"],
								},
							],
						},
					],
				},
			{
				progID: 3,
				title: "AI",
				progName: "ai.exe",
				minimized: true,
				maximized: false,
				invisibleToHash: true,
				component: ProgramGroup,
				icon: "▦",
				params: { _onOpenWindow: "!!handleOpenWindow" },
				initialPosition: {
					x: 5,
					y: 5,
					width: 350,
					height: 200,
					smHeight: 190,
				},
				defaultOpen: true,
				unCloseable: true,
				programs: [
					{
						// WSGopher-style browser for the Gopher-over-HTTPS
						// agent/MCP discovery convention (seneschal.space/gopher).
						// Fetched straight from the browser — the discovery
						// cards send permissive CORS, so no proxy is needed.
						progID: 1,
						title: "Gopher",
						titleNote: "Discovery",
						icon: "🐹",
						progName: "gopher.exe",
						component: GopherClient,
						initialPosition: {
							x: "auto",
							y: 0,
							width: 720,
							height: 520,
							smWidth: 360,
							smHeight: 460,
						},
					},
					{
						// Same in-app AI chat component as Accessories /
						// Money Manager; here it anchors the AI group.
						progID: 2,
						title: "Messenger",
						titleNote: "AI",
						icon: "💬",
						progName: "messenger.exe",
						component: MessengerApp,
						initialPosition: {
							x: "auto",
							y: 0,
							width: 640,
							height: 480,
							smWidth: 360,
							smHeight: 460,
						},
					},
					{
						// "General MCP info" — opens the Help app straight at
						// the Agent Access (MCP) topic.
						progID: 3,
						title: "MCP Help",
						titleNote: "Agent Access",
						icon: "🤖",
						progName: "winhelp.exe",
						component: HelpApp,
						params: { topic: "mcp" },
						initialPosition: {
							x: "auto",
							y: 0,
							width: 560,
							height: 480,
							smHeight: 380,
							smWidth: 340,
						},
					},
				],
			},
		],
	},
	{
		progID: 5,
		title: "Desk",
			icon: "🖥️", // Example icon
			progName: "desk.exe", // Added name for "Paintbrush"
			component: Desk,
			initialPosition: { x: 1, y: 1, width: 425, height: 485 },
			menuOnly: true,
			menuLabel: "New Desk...",
		},
	];
};
